import { ReactNode, useEffect, useRef, useState } from "react";

import {
  CopyButton,
  CurrencyDisplay,
  LeftRightFlexedContainer,
  ModalSectionTitle,
  SwitchAppContentSwitcher,
  Toggle,
} from "../../../../components";
import { IViewCustomer } from "../../../../interfaces-and-types";
import { useDisclose } from "../../../../hooks";
import { IDestinationTxOrInvoice } from "./RedirectToInvoiceForm";
import moment from "moment";
import { appConfig } from "../../../../configs";
import { Badge } from "@carbon/icons-react";

export enum redirectToInvoiceResolutionViewModes {
  selectTargetPaymentView = "selectTargetPaymentView",
  selectTargetInvoiceView = "selectTargetInvoiceView",
}

export const RedirectToPaymentIssueToInvoiceViewModeSelector = (props: {
  defaultMode?: redirectToInvoiceResolutionViewModes;
  selectedTxOrInvoice: IDestinationTxOrInvoice | undefined;
  targetCustomer?: IViewCustomer;
  selectTargetPaymentView: ReactNode;
  selectTargetInvoiceView: ReactNode;
}) => {
  const [viewMode, setViewMode] =
    useState<redirectToInvoiceResolutionViewModes>(
      redirectToInvoiceResolutionViewModes.selectTargetPaymentView
    );
  const {
    isOpen: viewTables,
    onClose,
    onToggle: toggleViewTables,
  } = useDisclose(true);

  const timeoutRef = useRef<NodeJS.Timeout | null>();
  useEffect(() => {
    if (!!props.selectedTxOrInvoice) {
      timeoutRef.current = setTimeout(onClose, 1400);
    }

    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
  }, [props.selectedTxOrInvoice]);

  return (
    <>
      <div className="mb-5 pl-5 pr-5 pt-5 pb-5 bordered border-dotted b-r-5">
        <ModalSectionTitle key_id="Customer Details" value={""} />

        {!props.targetCustomer ? null : (
          <>
            <LeftRightFlexedContainer
              key_id="Customer Name"
              value={props.targetCustomer.full_name || "(Anonymous)"}
            />
            <LeftRightFlexedContainer
              key_id="Customer email"
              value={
                <div className="flex-container justify-content--start align-items--center no-wrap">
                  <div className="transaction-id-text modal-header--title">
                    <span title={props.targetCustomer.email}>
                      {props.targetCustomer.email}
                    </span>
                  </div>
                  <div className="copy-button--container ml-3">
                    <CopyButton
                      // name="copy-customer-button"
                      stringToCopy={props.targetCustomer.email || "--"}
                      iconDescription="copy delivery email"
                      className="copy-btn"
                    />
                  </div>
                </div>
              }
            />
          </>
        )}

        <ModalSectionTitle
          key_id="Selected Destination Payment/Invoice Details"
          value={""}
        />

        {!props.selectedTxOrInvoice ? (
          <span>No payment/invoice selected...</span>
        ) : (
          <>
            <LeftRightFlexedContainer
              key_id="REF"
              value={props.selectedTxOrInvoice.tx_ref || "(Anonymous)"}
            />
            <LeftRightFlexedContainer
              key_id="Amount"
              value={
                <>
                  <CurrencyDisplay
                    currencyOrSymbol={props.selectedTxOrInvoice.currency}
                    amount={props.selectedTxOrInvoice.amount}
                    amountFontSize={16}
                    symbolFontSize={12}
                  />{" "}
                  ({props.selectedTxOrInvoice.currency})
                </>
              }
            />
            <LeftRightFlexedContainer
              key_id="Status"
              value={
                <strong className="bordered b-r-5 pl-5 pr-5">
                  {props.selectedTxOrInvoice.status.toUpperCase()}
                </strong>
              }
            />
            <LeftRightFlexedContainer
              key_id="Date"
              value={
                <span className="date--oneline">
                  {moment(props.selectedTxOrInvoice.created_at).format(
                    appConfig.dateTimeFormat
                  )}
                </span>
              }
            />
          </>
        )}
      </div>

      {!props.selectedTxOrInvoice ? null : (
        <Toggle
          name="toggleViewTables"
          size="sm"
          labelOff={
            <strong>
              {!viewTables ? "Show" : "Hide"} Payments/Invoices Table
            </strong>
          }
          labelOn={
            <strong>
              {!viewTables ? "Show" : "Hide"} Payments/Invoices Table
            </strong>
          }
          labelRight
          onToggle={toggleViewTables}
          title="Toggle View Payments/Invoices tables"
        />
      )}

      {!viewTables ? null : (
        <>
          <ModalSectionTitle
            key_id={
              <>
                Select a payment or invoice{" "}
                <span className="text-primary">(for the same customer)</span>{" "}
                from the lists below to redirect the payment into
              </>
            }
            value={""}
          />

          <SwitchAppContentSwitcher<redirectToInvoiceResolutionViewModes>
            onChange={({ name }) => {
              setViewMode(name);
            }}
            options={[
              {
                key: "Redirect To Payment",
                value:
                  redirectToInvoiceResolutionViewModes.selectTargetPaymentView,
              },
              {
                key: "Redirect To Invoice",
                value:
                  redirectToInvoiceResolutionViewModes.selectTargetInvoiceView,
              },
            ]}
            selectedValue={viewMode}
          />

          <div className="mt-5">
            {viewMode ===
            redirectToInvoiceResolutionViewModes.selectTargetPaymentView
              ? props.selectTargetPaymentView
              : props.selectTargetInvoiceView}
          </div>
        </>
      )}
    </>
  );
};
